import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import axios from "axios";

export type PropertyImage = {
  url: string;
  id: string;
};

export type InvestmentDetails = {
  type: "Résidentiel" | "Commercial" | "Touristique";
  returnRate: string;
  minEntryPrice: string;
  recommendedDuration: string;
  financingEligibility: boolean;
  partners: string[];
  projectStatus: "Pré-commercialisation" | "En cours" | "Terminé";
  documents?: string[];
  investmentType: string;
  mapLink?: string;
};

export type Property = {
  id: string;
  title: string;
  location: string;
  price: string;
  type: string;
  status: string;
  bedrooms: number;
  bathrooms: number;
  area: string;
  createdAt: string;
  description?: string;
  isFeatured?: boolean;
  return?: string;
  image?: string;
  documents?: string[];
  images?: PropertyImage[];
  isDraft?: boolean;
  isInvestment?: boolean;
  investmentDetails?: InvestmentDetails;
};

interface PropertiesContextType {
  properties: Property[];
  loading: boolean;
  error: string | null;
  setProperties: React.Dispatch<React.SetStateAction<Property[]>>;
  addProperty: (property: Property) => void;
  removeProperty: (id: string) => void;
  updateProperty: (id: string, property: Partial<Property>) => void;
  getPropertyById: (id: string) => Property | undefined;
  publishProperty: (id: string) => Promise<void>;
  draftProperty: (id: string) => Promise<void>;
}

const PropertiesContext = createContext<PropertiesContextType | undefined>(undefined);

export const useProperties = () => {
  const context = useContext(PropertiesContext);
  if (!context) {
    throw new Error("useProperties must be used within a PropertiesProvider");
  }
  return context;
};

interface PropertiesProviderProps {
  children: ReactNode;
}

export const PropertiesProvider: React.FC<PropertiesProviderProps> = ({ children }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const res = await axios.get("https://universelle.ma/api/properties");
        setProperties(res.data.data || []);
      } catch (err) {
        setError("Erreur lors du chargement des propriétés");
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const addProperty = (property: Property) => {
    setProperties(prev => [property, ...prev]);
  };

  const removeProperty = (id: string) => {
    setProperties(prev => prev.filter(p => p.id !== id));
  };

  const updateProperty = (id: string, updates: Partial<Property>) => {
    setProperties(prev =>
      prev.map(p => (p.id === id ? { ...p, ...updates } : p))
    );
  };

  const getPropertyById = (id: string) => {
    return properties.find(p => String(p.id) === String(id));
  };

  const buildPayload = (property: Property, isDraft: boolean) => {
  return {
    title: property.title,
    type: property.type,
    price: parseFloat(property.price),
    status: property.status,
    location: property.location,
    area: parseInt(property.area),
    bedrooms: parseInt(String(property.bedrooms)),
    bathrooms: parseInt(String(property.bathrooms)),
    description: property.description ?? "—",
    investmentType: property.investmentDetails?.investmentType ?? "",
    projectStatus: property.investmentDetails?.projectStatus ?? "",
    returnRate: parseFloat(property.investmentDetails?.returnRate ?? "0"),
    minEntryPrice: parseFloat(property.investmentDetails?.minEntryPrice ?? "0"),
    recommendedDuration: property.investmentDetails?.recommendedDuration ?? "",
    financingEligibility: !!property.investmentDetails?.financingEligibility,
    partners: property.investmentDetails?.partners ?? [],
    mapLink: property.investmentDetails?.mapLink ?? "",
    is_draft: isDraft,
    isFeatured: !!property.isFeatured,
    images: property.images ?? [],
    documents: property.documents ?? [],
  };
};


  const publishProperty = async (id: string) => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      alert("Token manquant. Veuillez vous reconnecter.");
      return;
    }
    const property = getPropertyById(id);
    if (!property) {
      alert("Propriété introuvable.");
      return;
    }

    try {
      const payload = buildPayload(property, false);
      console.log("⏩ Payload envoyé (publish):", payload);

      await axios.put(
  `https://universelle.ma/api/properties/${id}`,
  payload,
  {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  }
);

      updateProperty(id, { isDraft: false });
    } catch (error: any) {
      const backendError = error?.response?.data;
      console.error("❌ Erreur lors de la publication :", error);
      alert("Erreur: " + (backendError?.error || backendError?.message || "inconnue"));
    }
  };

  const draftProperty = async (id: string) => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      alert("Token manquant. Veuillez vous reconnecter.");
      return;
    }
    const property = getPropertyById(id);
    if (!property) {
      alert("Propriété introuvable.");
      return;
    }

    try {
      const payload = buildPayload(property, true);
      console.log("⏩ Payload envoyé (draft):", payload);

      await axios.put(
  `https://universelle.ma/api/properties/${id}`,
  payload,
  {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  }
);

      updateProperty(id, { isDraft: true });
    } catch (error: any) {
      const backendError = error?.response?.data;
      console.error("❌ Erreur lors de la mise en brouillon :", error);
      alert("Erreur: " + (backendError?.error || backendError?.message || "inconnue"));
    }
  };

  return (
    <PropertiesContext.Provider
      value={{
        properties,
        loading,
        error,
        setProperties,
        addProperty,
        removeProperty,
        updateProperty,
        getPropertyById,
        publishProperty,
        draftProperty,
      }}
    >
      {children}
    </PropertiesContext.Provider>
  );
};
